import { useEffect, useState } from "react";
import { Phone, MapPin, Navigation, Train, ArrowRight, Wifi, MonitorPlay, Laptop, Coffee, SquareParking, Headset, Users, Presentation, Mic2, CalendarCheck, Star, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import VenueBookingForm from "@/components/VenueBookingForm";

const HERO_IMG = "https://images.unsplash.com/photo-1776039230119-6e69e241512d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const SPACES = [
  { title: "Training Rooms", img: "https://images.unsplash.com/photo-1776512377708-6b4eb702505e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Classroom-style labs with projectors, whiteboards and high-end systems for hands-on batches." },
  { title: "Meeting & Boardrooms", img: "https://images.unsplash.com/photo-1777264935754-aa53096c2d68?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Professional setups for board meetings, reviews and client presentations." },
  { title: "Workshops & Seminars", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Flexible halls for seminars, summits, lectures and knowledge sessions." },
  { title: "Corporate Events", img: "https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", desc: "Town halls, press conferences, meetups and networking evenings." },
];

const FACILITY_ICONS = [Laptop, MonitorPlay, Wifi, Coffee, SquareParking, Headset];

const VENUE_PHOTOS = {
  "62": "https://images.unsplash.com/photo-1758691736591-5bf31a5d0dea?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "1": "https://images.unsplash.com/photo-1784217066863-9713160569f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
};

const dec = (s) => (s || "").replace(/<[^>]+>/g, " ").replace(/&#x27;|&#39;/g, "'").replace(/&quot;/g, '"').replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
const ulItems = (seg) => { const m = seg.match(/<ul[^>]*>([\s\S]*?)<\/ul>/i); return m ? [...m[1].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)].map((l) => dec(l[1])).filter(Boolean) : []; };
const imgAlt = (tag) => (tag.match(/alt=["']([^"']*)["']/i) || [])[1] || "";
const imgSrc = (tag) => (tag.match(/src=["']([^"']+)["']/i) || [])[1] || "";

export default function VenuePage({ lp }) {
  const raw = lp.body_html || lp.overview_html || "";
  const advIdx = raw.search(/<h2[^>]*>\s*Talk to our career advisors/i);
  const mapIdx = raw.search(/<h2[^>]*>\s*Map your Visit/i);
  const mainEnd = advIdx >= 0 ? advIdx : (mapIdx >= 0 ? mapIdx : raw.length);
  const main = raw.slice(0, mainEnd);
  const advisorsSeg = advIdx >= 0 ? raw.slice(advIdx, mapIdx > advIdx ? mapIdx : raw.length) : "";
  const mapSeg = mapIdx >= 0 ? raw.slice(mapIdx) : "";

  const segBetween = (fromRe, toRe) => {
    const a = main.search(fromRe);
    if (a < 0) return "";
    const rest = main.slice(a);
    const b = rest.slice(10).search(toRe);
    return b >= 0 ? rest.slice(0, b + 10) : rest;
  };

  const introH2 = dec((main.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i) || [])[1]);
  const venueFor = ulItems(segBetween(/<h4[^>]*>\s*Venue available for/i, /<h4[^>]*>\s*Facilities/i));
  const facilities = ulItems(segBetween(/<h4[^>]*>\s*Facilities/i, /<h3[^>]*>\s*The Training Basket Advantage/i));
  const advSeg = segBetween(/<h3[^>]*>\s*The Training Basket Advantage/i, /<h3[^>]*>\s*Why Us/i);
  const advTitle = dec((advSeg.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i) || [])[1]) || "The Training Basket Advantage";
  const advCards = [];
  {
    const parts = advSeg.split(/<img\b/i).slice(1);
    for (const part of parts) {
      const tag = "<img " + part;
      const src = imgSrc(tag);
      const h4 = dec((part.match(/<h4[^>]*>([\s\S]*?)<\/h4>/i) || [])[1]);
      const after = part.replace(/^[^>]*>/, "").replace(/<h4[^>]*>[\s\S]*?<\/h4>/i, "");
      const text = dec(after);
      if (src && h4) advCards.push({ src, h4, text });
    }
  }
  const whySeg = segBetween(/<h3[^>]*>\s*Why Us/i, /<h3[^>]*>\s*Partners/i);
  const whyIntro = dec(whySeg.replace(/<h3[^>]*>[\s\S]*?<\/h3>/i, "").split(/<ul/i)[0]);
  const whyItems = ulItems(whySeg);
  const whyH5 = dec((whySeg.match(/<h5[^>]*>([\s\S]*?)<\/h5>/i) || [])[1]);
  const whyP = dec((whySeg.match(/<p[^>]*>([\s\S]*?)<\/p>/i) || [])[1]);
  const partSeg = segBetween(/<h3[^>]*>\s*Partners/i, /<h3[^>]*>\s*We have served/i);
  const partners = [...partSeg.matchAll(/<img\b[^>]*>/gi)].map((m) => ({ src: imgSrc(m[0]), alt: imgAlt(m[0]) })).filter((x) => x.src);
  const corpSeg = segBetween(/<h3[^>]*>\s*We have served/i, /<h3[^>]*>\s*Process and Expertise/i);
  const corpTitle = dec((corpSeg.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i) || [])[1]);
  const corpLogos = [...corpSeg.matchAll(/<img\b[^>]*>/gi)].map((m) => ({ src: imgSrc(m[0]), alt: imgAlt(m[0]) })).filter((x) => x.src);
  const procSeg = segBetween(/<h3[^>]*>\s*Process and Expertise/i, /$^/);
  const procTitle = dec((procSeg.match(/<h3[^>]*>([\s\S]*?)<\/h3>/i) || [])[1]) || "Process and Expertise";
  const procIntro = dec(procSeg.replace(/<h3[^>]*>[\s\S]*?<\/h3>/i, "").split(/<h5/i)[0]);
  const procSteps = [...procSeg.matchAll(/<h5[^>]*>([\s\S]*?)<\/h5>([\s\S]*?)(?=<h5|$)/gi)].map((m) => ({ title: dec(m[1]), text: dec(m[2]) }));

  const advsTitle = dec((advisorsSeg.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i) || [])[1]) || "Talk to our career advisors now";
  const advsLead = dec(advisorsSeg.replace(/<h2[^>]*>[\s\S]*?<\/h2>/i, "").split(/<p/i)[0]);
  const advsP = dec((advisorsSeg.match(/<p[^>]*>([\s\S]*?)<\/p>/i) || [])[1]);
  const helplineBits = [...advisorsSeg.matchAll(/<h6[^>]*>([\s\S]*?)<\/h6>/gi)].map((m) => dec(m[1]));
  const mapTitle = dec((mapSeg.match(/<h2[^>]*>([\s\S]*?)<\/h2>/i) || [])[1]) || "Map your Visit to Us";
  const mapSub = dec((mapSeg.match(/<p[^>]*>([\s\S]*?)<\/p>/i) || [])[1]);
  const sectors = [...mapSeg.matchAll(/<h6[^>]*>([\s\S]*?)<\/h6>/gi)].map((m) => dec(m[1])).filter(Boolean);

  const venues = sectors.map((s) => {
    const num = s.match(/\d+/)?.[0] || "62";
    const q = `Training Basket, Sector ${num}, Noida`;
    return {
      name: s, num, q,
      metro: num === "62" ? "Noida Electronic City Metro Station" : "Noida Sector 16 Metro Station",
      photo: VENUE_PHOTOS[num] || VENUE_PHOTOS["62"],
    };
  });

  const facilityCards = [
    { icon: Laptop, title: "High-End Laptops & Desktops", text: facilities.slice(0, 3).join(" · ") },
    { icon: MonitorPlay, title: "Projectors & Audio", text: [facilities[3], facilities[4], facilities[5]].filter(Boolean).join(" · ") },
    { icon: Wifi, title: "High-Speed Internet", text: whyIntro },
    { icon: Coffee, title: "Catering & Coffee", text: [facilities[6], facilities[7], facilities[8]].filter(Boolean).join(" · ") },
    { icon: SquareParking, title: "Parking", text: facilities.find((f, i) => /parking/i.test(f)) || "" },
    { icon: Headset, title: "On-site Support Staff", text: facilities.find((f) => /supporting staff/i.test(f)) || "" },
  ].filter((c) => c.text);

  const [mapTab, setMapTab] = useState(0);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/website/sections/home_content").then((r) => {
      const ts = (r.data.testimonials || []).filter((t) => t.visible !== false && t.quote);
      setTestimonials(ts.slice(0, 3));
    }).catch(() => {});
  }, []);

  const cur = venues[mapTab] || venues[0];

  return (
    <div className="bg-white text-slate-900" data-testid="venue-page">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" data-testid="venue-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" />
              <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-slate-500">Corporate Venues</span>
            </div>
            <h1 className="m-0 mt-4 text-4xl sm:text-5xl lg:text-[3.2rem] font-extrabold tracking-tight leading-[1.08] text-[#17263B]">
              Corporate Venue &amp;{" "}
              <span className="relative inline-block">
                <span className="relative z-10">Team Offsites</span>
                <span className="absolute inset-x-0 bottom-1 h-4 bg-[#FFC72C] rounded-sm" aria-hidden="true" />
              </span>
            </h1>
            {introH2 && <h2 className="m-0 mt-4 text-lg sm:text-xl font-semibold leading-relaxed text-slate-500 max-w-xl">{introH2}</h2>}
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#venue-form" data-testid="venue-hero-book"
                className="inline-flex items-center gap-2 min-h-[52px] px-7 bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] rounded-full font-extrabold text-[15px] text-[#17263B] shadow-[0_10px_24px_rgba(255,199,44,.35)] transition-all">
                Book a Venue <ArrowRight className="w-[18px] h-[18px]" />
              </a>
              <a href="#venue-spaces" data-testid="venue-hero-explore"
                className="inline-flex items-center gap-2 min-h-[52px] px-6 bg-white border-[1.5px] border-slate-200 rounded-full font-bold text-[15px] text-[#17263B] hover:border-[#FFC72C] transition-colors">
                Explore Spaces
              </a>
              <a href="tel:+919015887887" data-testid="venue-hero-call"
                className="inline-flex items-center gap-2 min-h-[52px] px-6 text-[15px] font-bold text-amber-700 hover:text-amber-800 transition-colors">
                <Phone className="w-[18px] h-[18px]" /> Schedule a Call
              </a>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md" data-testid="venue-hero-stats">
              {["20–100 Seats Per Room", "24×7 Availability", "200+ Corporate Clients"].map((s, i) => (
                <div key={s} className="rounded-2xl bg-slate-50 border border-slate-100 px-3 py-3 text-center" data-testid={`venue-stat-${i}`}>
                  <p className="text-sm font-extrabold text-[#17263B] leading-tight">{s.split(" ").slice(0, 1)}</p>
                  <p className="text-[11px] font-semibold text-slate-500 mt-0.5 leading-tight">{s.split(" ").slice(1).join(" ")}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative min-w-0">
            <div className="absolute -top-10 -right-8 w-72 h-72 bg-[#FFC72C] rounded-full opacity-80 blur-3xl" aria-hidden="true" />
            <div className="relative rounded-[2.5rem] overflow-hidden border-4 border-[#FFC72C] shadow-[0_30px_60px_-20px_rgba(23,38,59,0.35)]">
              <img src={HERO_IMG} alt="Corporate training session in a Training Basket venue" className="w-full aspect-[4/3] object-cover" data-testid="venue-hero-image" />
            </div>
            <span className="absolute -left-3 sm:-left-5 top-8 flex items-center gap-1.5 bg-white rounded-full shadow-lg border border-slate-100 px-3.5 py-1.5 text-xs font-bold text-[#17263B]" data-testid="venue-hero-pill-0">
              <Users className="w-3.5 h-3.5 text-amber-600" /> 5–7 Rooms per Location
            </span>
            <span className="absolute -right-3 sm:-right-5 bottom-10 flex items-center gap-1.5 bg-white rounded-full shadow-lg border border-slate-100 px-3.5 py-1.5 text-xs font-bold text-[#17263B]" data-testid="venue-hero-pill-1">
              <CalendarCheck className="w-3.5 h-3.5 text-amber-600" /> 365 Days a Year
            </span>
          </div>
        </div>
      </section>

      {/* ============ FIND THE RIGHT SPACE ============ */}
      <section id="venue-spaces" className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 scroll-mt-24" data-testid="venue-spaces">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">Our Spaces</span>
          <h2 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">Find the Right Space for Your Team</h2>
          <p className="mt-3 text-[15px] text-slate-500">Venue available for</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SPACES.map((s, i) => (
            <a key={s.title} href="#venue-form" className="group rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-[0_20px_45px_-18px_rgba(23,38,59,0.3)] transition-all" data-testid={`venue-space-${i}`}>
              <div className="relative overflow-hidden">
                <img src={s.img} alt={s.title} loading="lazy" className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <p className="font-extrabold text-[15px] text-[#17263B] group-hover:text-amber-700 transition-colors">{s.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{s.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-extrabold text-amber-700">Check availability <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </a>
          ))}
        </div>
        {venueFor.length > 0 && (
          <div className="mt-7 flex flex-wrap justify-center gap-2" data-testid="venue-space-all">
            {venueFor.map((v, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3.5 py-1.5" data-testid={`venue-space-chip-${i}`}>
                <CheckCircle2 className="w-3.5 h-3.5 text-amber-600" />{v}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* ============ FACILITIES ============ */}
      <section className="bg-[#0F1115] text-white" data-testid="venue-facilities">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#FFC72C]">Infrastructure &amp; Facilities</span>
            <h2 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight">Everything You Need, All in One Place</h2>
            <p className="mt-3 text-[15px] text-slate-400">Facilities</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {facilityCards.map((f, i) => (
              <div key={f.title} className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 hover:border-[#FFC72C]/50 hover:bg-white/[0.07] transition-all" data-testid={`venue-facility-${i}`}>
                <span className="w-12 h-12 rounded-2xl bg-[#FFC72C] flex items-center justify-center"><f.icon className="w-5.5 h-5.5 text-[#17263B]" /></span>
                <p className="mt-4 font-extrabold text-[16px]">{f.title}</p>
                <p className="mt-1.5 text-[13px] leading-relaxed text-slate-400">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EXPLORE OUR VENUES ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-testid="venue-locations">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">Locations</span>
          <h2 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">Explore Our Venues</h2>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {venues.map((v, i) => (
            <div key={v.name} className="group rounded-3xl overflow-hidden border border-slate-200 bg-white shadow-[0_18px_40px_-22px_rgba(23,38,59,0.25)]" data-testid={`venue-location-${i}`}>
              <div className="relative overflow-hidden">
                <img src={v.photo} alt={`Training Basket venue — ${v.name}`} loading="lazy" className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-3.5 left-3.5 bg-[#FFC72C] text-[#17263B] text-[11px] font-extrabold uppercase tracking-wider px-3 py-1.5 rounded-full">Noida</span>
              </div>
              <div className="p-6">
                <p className="font-extrabold text-lg text-[#17263B] flex items-center gap-2"><MapPin className="w-4.5 h-4.5 text-amber-600 shrink-0" />{v.name}</p>
                <p className="mt-2 text-[13px] text-slate-500 leading-relaxed">20–100 seats per room · 5–7 rooms · Metro-connected · Basement parking</p>
                <button onClick={() => { setMapTab(i); document.querySelector("[data-testid='venue-map']")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-[1.5px] border-[#FFC72C] text-[#17263B] text-sm font-extrabold hover:bg-[#FFC72C] transition-colors"
                  data-testid={`venue-location-map-${i}`}>
                  View Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ ADVANTAGE ============ */}
      {advCards.length > 0 && (
        <section className="bg-slate-50/70 border-y border-slate-100" data-testid="venue-advantage">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">Why Training Basket</span>
              <h3 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">{advTitle}</h3>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {advCards.map((c, i) => (
                <div key={i} className="rounded-3xl border border-slate-200 bg-white p-6 hover:border-amber-300 hover:shadow-[0_16px_36px_-18px_rgba(217,161,27,0.4)] transition-all" data-testid={`venue-adv-${i}`}>
                  <img src={c.src} alt="" loading="lazy" className="w-12 h-12 object-contain" />
                  <h4 className="m-0 mt-4 font-extrabold text-[16px] text-[#17263B]">{c.h4}</h4>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{c.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ TRUSTED BY + TESTIMONIALS ============ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18" data-testid="venue-trusted">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">Trusted by Corporate Teams</span>
          {corpTitle && <h3 className="m-0 mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#17263B]">{corpTitle}</h3>}
        </div>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-x-10 gap-y-6" data-testid="venue-client-logos">
          {[...corpLogos, ...partners].map((l, i) => (
            <img key={i} src={l.src} alt={l.alt} loading="lazy" className="h-9 sm:h-11 w-auto max-w-[130px] object-contain grayscale hover:grayscale-0 opacity-80 hover:opacity-100 transition-all" data-testid={`venue-logo-${i}`} />
          ))}
        </div>
        {testimonials.length > 0 && (
          <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="venue-testimonials">
            {testimonials.map((t, i) => (
              <figure key={i} className="rounded-3xl border border-slate-200 bg-white p-6 flex flex-col" data-testid={`venue-testimonial-${i}`}>
                <div className="flex gap-0.5" aria-label="5 star rating">
                  {Array.from({ length: 5 }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#FFC72C] text-[#FFC72C]" />)}
                </div>
                <blockquote className="mt-3.5 text-sm leading-relaxed text-slate-600 flex-1">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  {t.photo && <img src={t.photo} alt={t.name} loading="lazy" className="w-11 h-11 rounded-full object-cover border-2 border-[#FFC72C]" />}
                  <span>
                    <span className="block font-extrabold text-sm text-[#17263B]">{t.name}</span>
                    <span className="block text-xs text-slate-500">{[t.role, t.company].filter(Boolean).join(", ")}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </section>

      {/* ============ PROCESS ============ */}
      {procSteps.length > 0 && (
        <section className="bg-slate-50/70 border-y border-slate-100" data-testid="venue-process">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18">
            <div className="text-center max-w-2xl mx-auto">
              <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">How It Works</span>
              <h3 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">Simple Booking Process</h3>
              <p className="mt-2 text-sm font-extrabold uppercase tracking-wider text-slate-400">{procTitle}</p>
              {procIntro && <p className="mt-3 text-[15px] text-slate-500">{procIntro}</p>}
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {procSteps.slice(0, 4).map((s, i) => (
                <div key={i} className="relative rounded-3xl border border-slate-200 bg-white p-6" data-testid={`venue-step-${i}`}>
                  <span className="w-11 h-11 rounded-full bg-[#FFC72C] text-[#17263B] font-extrabold text-lg flex items-center justify-center">{i + 1}</span>
                  <h4 className="m-0 mt-4 font-extrabold text-[16px] text-[#17263B]">{s.title}</h4>
                  <p className="mt-1.5 text-[13px] leading-relaxed text-slate-500">{s.text}</p>
                  {i < procSteps.slice(0, 4).length - 1 && <ArrowRight className="hidden lg:block absolute top-1/2 -right-4 w-5 h-5 text-amber-500 -translate-y-1/2 z-10" aria-hidden="true" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============ WHY US ============ */}
      {whyItems.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18" data-testid="venue-why-us">
          <div className="grid lg:grid-cols-2 gap-10 items-start max-w-6xl mx-auto">
            <div>
              <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">Why Us</span>
              <h3 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">Why Us</h3>
              {whyIntro && <p className="mt-4 text-[15px] leading-relaxed text-slate-500">{whyIntro}</p>}
              {whyH5 && <p className="mt-6 text-lg font-extrabold text-[#17263B]">{whyH5}</p>}
              {whyP && <p className="mt-2 text-[15px] leading-relaxed text-slate-500">{whyP}</p>}
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {whyItems.map((w, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm font-semibold text-slate-700" data-testid={`venue-why-${i}`}>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> {w}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ============ CTA + BOOKING FORM ============ */}
      <section id="venue-form" className="bg-[#0F1115] text-white scroll-mt-24" data-testid="venue-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="min-w-0">
            <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-[#FFC72C]">Book Your Venue</span>
            <h2 className="m-0 mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">Plan Your Next Corporate Event With Us</h2>
            {advsTitle && <p className="mt-3 text-lg font-extrabold text-[#FFC72C]">{advsTitle}</p>}
            {advsLead && <p className="mt-4 text-[15px] leading-relaxed text-slate-300">{advsLead}</p>}
            {advsP && <p className="mt-1 text-[15px] leading-relaxed text-slate-300">{advsP}</p>}
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["Premium Locations", "Flexible Spaces", "24×7 Support"].map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-white/10 border border-white/15 rounded-full px-3.5 py-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#FFC72C]" />{c}
                </span>
              ))}
            </div>
            <a href="tel:+919015887887" className="mt-7 flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 hover:bg-white/10 transition-colors max-w-sm" data-testid="venue-helpline">
              <span className="w-10 h-10 rounded-full bg-[#FFC72C] text-[#17263B] flex items-center justify-center shrink-0"><Phone className="w-4.5 h-4.5" /></span>
              <span>
                <span className="block text-xs text-slate-400 font-semibold">{helplineBits[0] || "Call us on our helpline"}</span>
                <span className="block text-base font-extrabold">{helplineBits[1] || "+91-9015 887 887"}</span>
              </span>
            </a>
          </div>
          <div className="min-w-0 [&_h2]:!text-[#17263B] [&_h2]:!text-2xl [&>div]:!bg-white [&>div]:!text-slate-900" data-testid="venue-form-wrap">
            <div className="rounded-3xl overflow-hidden shadow-[0_30px_70px_-25px_rgba(0,0,0,0.6)]">
              <VenueBookingForm path={lp.url_path} />
            </div>
          </div>
        </div>
      </section>

      {/* ============ MAP ============ */}
      {venues.length > 0 && cur && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18" data-testid="venue-map">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-extrabold uppercase tracking-[0.22em] text-amber-700">Find Us</span>
            <h2 className="m-0 mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]">{mapTitle}</h2>
            {mapSub && <p className="mt-3 text-[15px] text-slate-500">{mapSub}</p>}
          </div>
          <div className="mt-8 max-w-4xl mx-auto">
            <div className="flex flex-wrap justify-center gap-2" role="tablist" aria-label="Venue locations">
              {venues.map((v, i) => (
                <button key={v.name} role="tab" aria-selected={mapTab === i} onClick={() => setMapTab(i)}
                  className={`px-4 py-2 rounded-full text-[13px] font-extrabold border transition-all ${mapTab === i ? "bg-[#17263B] text-white border-[#17263B]" : "bg-white text-slate-600 border-slate-200 hover:border-[#FFC72C]"}`}
                  data-testid={`venue-map-tab-${i}`}>
                  {v.name}
                </button>
              ))}
            </div>
            <div className="mt-5 rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_45px_-20px_rgba(23,38,59,0.25)]">
              <iframe key={cur.q} title={`Map — ${cur.name}`} src={`https://www.google.com/maps?q=${encodeURIComponent(cur.q)}&output=embed`}
                loading="lazy" className="w-full h-[320px] border-0" data-testid="venue-map-frame" />
            </div>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(cur.q)}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] text-[#17263B] text-sm font-extrabold transition-colors" data-testid="venue-directions">
                <Navigation className="w-4 h-4" /> Get Directions (Google Maps)
              </a>
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cur.metro)}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-[1.5px] border-slate-200 hover:border-[#FFC72C] text-[#17263B] text-sm font-bold transition-colors" data-testid="venue-metro">
                <Train className="w-4 h-4 text-amber-600" /> Find Nearest Metro
              </a>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
